import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { useSite } from '../context/SiteContext';
import { getClientReceivableValue } from '../utils/balanceValues';
import { isGenericConsumidorFinalClient } from '../utils/consumidorFinal';
import { getProviderBalanceValue } from '../utils/providerBalances';
import { fetchAllPaginated, getProductStockForSite } from '../utils/productCatalog';
import { getCatalogSalePrice, getCatalogUnitCost } from '../utils/catalogPricing';
import { armarSerieEquilibrio, calcularPuntoEquilibrio, incluyeGastosVariables } from '../utils/puntoEquilibrio';
import {
  PERIODS,
  SALE_STATUS_NOT_IN,
  buildDailySeries,
  buildPresetRange,
  buildPrevRangeDates,
  buildRangeFromDates,
  buildSaleQueryBounds,
  deltaPercent,
  getCompraDueDate,
  getSaleDateKey,
  computeComparativaSucursales,
  computeMetrics,
  computeRentabilidadProductos,
  computeRotacion,
  isDateKeyInRange,
} from '../utils/reportesInteligentes';
import {
  atribuirVentasATurnos,
  computeArqueos,
  computeDesvioPorCajero,
  computeResumenArqueos,
  computeVentasPorHora,
} from '../utils/reportesCaja';
import useCashCloseAccess from '../hooks/useCashCloseAccess';
import {
  MOVIMIENTO_TONE,
  PIE_COLORS,
  classifyTipoMovimiento,
  formatDateShort,
  isMermaMovimiento,
  safeName,
} from './reportes-inteligentes/formato';
import {
  AlertCard,
  DataTable,
  EmptyState,
  KpiCard,
  SectionCard,
} from './reportes-inteligentes/componentes';
import TabInventario from './reportes-inteligentes/TabInventario';
import TabCaja from './reportes-inteligentes/TabCaja';
import TabResumen from './reportes-inteligentes/TabResumen';
import TabProduccion from './reportes-inteligentes/TabProduccion';
import TabRentabilidad from './reportes-inteligentes/TabRentabilidad';
import {
  AlertTriangle,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BarChart3,
  Boxes,
  CalendarClock,
  ClipboardList,
  Clock,
  CreditCard,
  DollarSign,
  Download,
  Factory,
  FileSpreadsheet,
  History,
  Layers,
  LayoutList,
  MapPin,
  Package,
  Percent,
  RefreshCw,
  Repeat,
  Scale,
  ShoppingCart,
  Sparkles,
  Tag,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  Truck,
  Users,
  Wallet,
  Warehouse,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Chart from '../components/Chart';

// ---------- Configuracion ----------

// ---------- Helpers ----------

// ---------- Sub-componentes ----------

// ---------- Componente principal ----------
export default function ReportesInteligentes() {
  const navigate = useNavigate();
  const { profile, organization } = useAuth();
  const peIncluyeVariables = incluyeGastosVariables(organization);
  const { sites } = useSite();
  // Cierre ciego: el mismo hook que usa Caja.jsx. Un reporte no puede ser la
  // puerta trasera de un permiso — ver spec-permisos-caja-cierre-ciego.
  const { canViewFullClose, blindCashCloseEnabled, resolvingCashCloseAccess } =
    useCashCloseAccess();
  const orgId = profile?.organization_id;

  // Toggle chart ↔ tabla por sección
  const [showPrevSeries, setShowPrevSeries] = useState(false);
  const [viewTopProductos, setViewTopProductos] = useState('chart');
  const [viewTopClientes, setViewTopClientes] = useState('chart');
  const [viewUnidades, setViewUnidades] = useState('chart');
  const [viewDeudaProveedores, setViewDeudaProveedores] = useState('chart');
  const [viewCategorias, setViewCategorias] = useState('chart');

  // Navegación: pestaña principal (resumen ↔ productos) y sub-pestaña de productos.
  const [mainTab, setMainTab] = useState('resumen');
  const [productSubTab, setProductSubTab] = useState('inventario');
  const [kardexProductId, setKardexProductId] = useState('');
  const [kardexQuery, setKardexQuery] = useState('');

  // Guard anti-carrera: solo la última invocación de loadData aplica resultados.
  // Evita que una respuesta lenta de un rango anterior pise a la del filtro actual.
  const loadSeqRef = useRef(0);

  const [period, setPeriod] = useState('today');
  const initialRange = useMemo(() => buildPresetRange(PERIODS.find((p) => p.id === 'today')), []);
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);
  const [siteFilter, setSiteFilter] = useState(''); // '' = consolidado
  const [showPromoProducts, setShowPromoProducts] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [raw, setRaw] = useState({
    sales: [],
    saleItems: [],
    prevSalesTotal: 0,
    prevCmv: 0,
    prevGastosTotal: 0,
    prevDaily: [],
    products: [],
    clients: [],
    providers: [],
    // NOTA: las compras del periodo (para evolucion de costos y compras por
    // proveedor) se sacaron: se traian en cada refresh y no las consumia ningun
    // useMemo. Vuelven con la pestana Compras — ver spec-reportes-inteligentes-v2 (F2.5).
    openCompras: [],
    gastos: [],
  });

  // Snapshot operativo (mismo origen que el Dashboard): saldo de bancos,
  // pedidos web pendientes, productos con margen bajo, vencimientos del día.
  // Estos valores NO dependen del rango de fechas elegido (son estado actual)
  // y respetan el siteFilter — cuando está vacío, devuelven consolidado.
  const [snapshot, setSnapshot] = useState({
    saldoCaja: 0,
    bancosNegativos: 0,
    pedidosWeb: 0,
    bajaRentabilidad: 0,
    proyIngresosHoy: 0,
    proyEgresosHoy: 0,
  });

  // Datos pesados que sólo se cargan cuando el usuario entra a una sub-pestaña
  // de Productos que los necesita (no frenan el dashboard de Resumen).
  // movimientos: kardex; produccion + recetas: reportes de Producción.
  const [extra, setExtra] = useState({ movimientos: [], produccion: [], recetas: [], turnos: [] });
  const [extraLoaded, setExtraLoaded] = useState({ movimientos: false, produccion: false, turnos: false });
  const [extraLoading, setExtraLoading] = useState({ movimientos: false, produccion: false, turnos: false });

  const range = useMemo(() => buildRangeFromDates(dateFrom, dateTo), [dateFrom, dateTo]);
  const periodLabel = useMemo(() => {
    if (period === 'custom') return `${dateFrom} → ${dateTo}`;
    return PERIODS.find((p) => p.id === period)?.label || `${dateFrom} → ${dateTo}`;
  }, [period, dateFrom, dateTo]);
  const selectedSite = useMemo(
    () => sites.find((s) => String(s.id) === String(siteFilter)) || null,
    [sites, siteFilter],
  );

  const applyPreset = (id) => {
    const cfg = PERIODS.find((p) => p.id === id);
    if (!cfg) return;
    const r = buildPresetRange(cfg);
    setPeriod(id);
    setDateFrom(r.from);
    setDateTo(r.to);
  };

  const handleDateChange = (which, value) => {
    if (!value) return;
    if (which === 'from') {
      const safe = value > dateTo ? dateTo : value;
      setDateFrom(safe);
    } else {
      const safe = value < dateFrom ? dateFrom : value;
      setDateTo(safe);
    }
    setPeriod('custom');
  };

  const syncPresetRange = useCallback(() => {
    if (period === 'custom') return false;
    const cfg = PERIODS.find((p) => p.id === period);
    if (!cfg) return false;
    const next = buildPresetRange(cfg);
    if (next.from === dateFrom && next.to === dateTo) return false;
    setDateFrom(next.from);
    setDateTo(next.to);
    return true;
  }, [period, dateFrom, dateTo]);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (!orgId) return;
      if (!dateFrom || !dateTo) return;
      const seq = (loadSeqRef.current += 1);
      if (silent) setRefreshing(true);
      else setLoading(true);

      try {
        const saleBounds = buildSaleQueryBounds(dateFrom, dateTo);
        const { prevStartDate, prevEndDate } = buildPrevRangeDates(dateFrom, dateTo, period);
        const prevSaleBounds = buildSaleQueryBounds(prevStartDate, prevEndDate);
        const startDate = dateFrom;
        const endDate = dateTo;
        const hasSiteFilter = Boolean(siteFilter);

        const applySaleSiteFilter = (q) => (hasSiteFilter ? q.eq('site_id', siteFilter) : q);
        const applyItemsSiteFilter = (q) =>
          hasSiteFilter ? q.eq('venta.site_id', siteFilter) : q;

        // Wrapper que pagina con fetchAllPaginated pero degrada parcialmente:
        // si una query individual falla, loguea con contexto y devuelve []
        // para que el resto del dashboard se siga renderizando.
        const safeFetchAll = async (label, buildQuery) => {
          try {
            return await fetchAllPaginated(buildQuery);
          } catch (e) {
            console.error(
              `[ReportesInteligentes] ${label} falló:`,
              e?.message || e,
              { code: e?.code, details: e?.details, hint: e?.hint },
            );
            return [];
          }
        };
        const applyGastosSiteFilter = (q) => (hasSiteFilter ? q.eq('sitio_id', siteFilter) : q);

        const [
          rawSales,
          rawSaleItems,
          rawPrevSales,
          rawPrevItems,
          products,
          clients,
          providers,
          openCompras,
          rawGastos,
          snapshotRes,
        ] = await Promise.all([
          safeFetchAll('sales', () =>
            applySaleSiteFilter(
              supabase
                .from('sales')
                .select('id, total, created_at, fecha_venta, client_id, status, site_id, afecta_caja, payment_methods(name)')
                .eq('organization_id', orgId)
                .gte('created_at', saleBounds.startIso)
                .lte('created_at', saleBounds.endIso)
                .not('status', 'in', SALE_STATUS_NOT_IN)
                .order('created_at', { ascending: false })
                .order('id', { ascending: true }),
            ),
          ),
          safeFetchAll('sale_items', () =>
            applyItemsSiteFilter(
              supabase
                .from('sale_items')
                .select(
                  'id, sale_id, quantity, unit_price, subtotal, product_name_snapshot, variant_id, combo_role, parent_combo_product_id, producto:products!vinc_prod(id, name, cost_price), venta:sales!vinc_sale!inner(id, total, created_at, fecha_venta, organization_id, status, client_id, site_id)',
                )
                .eq('venta.organization_id', orgId)
                .gte('venta.created_at', saleBounds.startIso)
                .lte('venta.created_at', saleBounds.endIso)
                .not('venta.status', 'in', SALE_STATUS_NOT_IN)
                .order('id', { ascending: true }),
            ),
          ),
          safeFetchAll('sales (período anterior)', () =>
            applySaleSiteFilter(
              supabase
                .from('sales')
                .select('id, total, created_at, fecha_venta, status, site_id')
                .eq('organization_id', orgId)
                .gte('created_at', prevSaleBounds.startIso)
                .lte('created_at', prevSaleBounds.endIso)
                .not('status', 'in', SALE_STATUS_NOT_IN)
                .order('created_at', { ascending: false })
                .order('id', { ascending: true }),
            ),
          ),
          safeFetchAll('sale_items (período anterior)', () =>
            applyItemsSiteFilter(
              supabase
                .from('sale_items')
                .select(
                  'id, quantity, combo_role, producto:products!vinc_prod(cost_price), venta:sales!vinc_sale!inner(created_at, fecha_venta, organization_id, status, site_id)',
                )
                .eq('venta.organization_id', orgId)
                .gte('venta.created_at', prevSaleBounds.startIso)
                .lte('venta.created_at', prevSaleBounds.endIso)
                .not('venta.status', 'in', SALE_STATUS_NOT_IN)
                .order('id', { ascending: true }),
            ),
          ),
          safeFetchAll('products', () =>
            supabase
              .from('products')
              .select('id, name, brand, cost_price, sell_price, price_usd, priced_in_usd, category_id, min_stock, active, created_at, product_categories(name), product_variants(price, cost_price), stock_levels(quantity, site_id, min_stock)')
              .eq('organization_id', orgId)
              .eq('active', true)
              .order('name', { ascending: true })
              .order('id', { ascending: true }),
          ),
          safeFetchAll('clients', () =>
            supabase
              .from('clients')
              // NOTA: la tabla clients no tiene column current_balance en esta DB.
              // getClientBalanceValue() ya hace fallback a balance ?? 0.
              .select('id, full_name, balance, doc_number')
              .eq('organization_id', orgId)
              .eq('is_active', true)
              .order('full_name', { ascending: true })
              .order('id', { ascending: true }),
          ),
          safeFetchAll('proveedores', () =>
            supabase
              .from('proveedores')
              .select('id, razon_social, saldo')
              .eq('organization_id', orgId)
              .eq('is_active', true)
              .order('razon_social', { ascending: true })
              .order('id', { ascending: true }),
          ),
          // Compras abiertas (todas las pendientes de pago, sin filtro de fecha)
          // para calcular vencimientos próximos por proveedor. Excluimos las que
          // ya están pagadas o anuladas y los recibos de pago (que no son deudas).
          safeFetchAll('compras abiertas', () =>
            supabase
              .from('compras')
              .select('id, total_general, fecha, condicion_pago, estado, tipo_comprobante, proveedor_id, proveedor_nombre')
              .eq('organization_id', orgId)
              .not('estado', 'in', '("Pagada","pagada","PAGADA","ANULADO","Anulado","anulado","anulada")')
              .order('fecha', { ascending: false })
              .order('id', { ascending: true }),
          ),
          // Rango ampliado al periodo anterior para poder darle delta al
          // resultado neto. Es UNA query que cubre los dos tramos y se parte en
          // memoria: una segunda query seria otro round-trip por el mismo dato.
          safeFetchAll('gastos', () =>
            applyGastosSiteFilter(
              supabase
                .from('gastos')
                .select('id, monto, categoria, fecha, sitio_id')
                .eq('organization_id', orgId)
                .gte('fecha', prevStartDate < startDate ? prevStartDate : startDate)
                .lte('fecha', endDate)
                .eq('is_active', true)
                .order('fecha', { ascending: false })
                .order('id', { ascending: true }),
            ),
          ),
          // Snapshot operativo (saldo de bancos, pedidos web pendientes,
          // baja rentabilidad, vencimientos del día) calculado del lado de
          // la DB con la MISMA RPC que usa el Dashboard. Así garantizamos
          // que los números coincidan en ambos módulos.
          supabase.rpc('get_dashboard_metrics', {
            p_organization_id: orgId,
            p_site_id: hasSiteFilter ? siteFilter : null,
          }),
        ]);

        // Si mientras tanto arrancó una carga más nueva (cambió fecha/sucursal),
        // descartamos estos resultados para no pisar al filtro vigente.
        if (loadSeqRef.current !== seq) return;

        const filterSalesByDate = (rows, from, to) =>
          (rows || []).filter((sale) => isDateKeyInRange(getSaleDateKey(sale), from, to));
        const filterSaleItemsByDate = (rows, from, to) =>
          (rows || []).filter((item) => isDateKeyInRange(getSaleDateKey(item?.venta), from, to));

        // gastos.fecha es columna date ('YYYY-MM-DD'); si viniera con hora,
        // el slice deja la clave comparable igual que el resto de los rangos.
        const gastoDateKey = (g) => String(g?.fecha || '').slice(0, 10);
        const filterGastosByDate = (rows, from, to) =>
          (rows || []).filter((g) => isDateKeyInRange(gastoDateKey(g), from, to));

        const sales = filterSalesByDate(rawSales, dateFrom, dateTo);
        const saleItems = filterSaleItemsByDate(rawSaleItems, dateFrom, dateTo);
        const prevSales = filterSalesByDate(rawPrevSales, prevStartDate, prevEndDate);
        const prevItems = filterSaleItemsByDate(rawPrevItems, prevStartDate, prevEndDate);
        // Serie diaria del tramo anterior para superponer en el grafico. Se
        // guarda ya agregada (un objeto por dia) y no las ventas crudas: el
        // grafico es lo unico que la consume.
        const prevDaily = buildDailySeries(prevSales, prevStartDate, prevEndDate);
        const gastosPeriodo = filterGastosByDate(rawGastos, dateFrom, dateTo);
        const prevGastosTotal = filterGastosByDate(rawGastos, prevStartDate, prevEndDate).reduce(
          (acc, g) => acc + Number(g.monto || 0),
          0,
        );

        // Snapshot también degrada parcialmente: si falla, dejamos los valores
        // en 0 en vez de bricar todo el dashboard.
        if (snapshotRes?.error) {
          console.error(
            '[ReportesInteligentes] snapshot (get_dashboard_metrics) falló:',
            snapshotRes.error?.message || snapshotRes.error,
            { code: snapshotRes.error?.code, details: snapshotRes.error?.details, hint: snapshotRes.error?.hint },
          );
        }

        const snapshotData = snapshotRes?.data || {};
        setSnapshot({
          saldoCaja: Number(snapshotData.saldoCaja || 0),
          bancosNegativos: Number(snapshotData.bancosNegativos || 0),
          pedidosWeb: Number(snapshotData.pedidosWeb || 0),
          bajaRentabilidad: Number(snapshotData.bajaRentabilidad || 0),
          proyIngresosHoy: Number(snapshotData.proyIngresosHoy || 0),
          proyEgresosHoy: Number(snapshotData.proyEgresosHoy || 0),
        });

        const prevSalesTotal = prevSales.reduce(
          (acc, s) => acc + Number(s.total || 0),
          0,
        );
        const prevCmv = prevItems.reduce((acc, item) => {
          if (item.combo_role === 'parent') return acc;
          const qty = Number(item.quantity || 0);
          const cost = Number(item.producto?.cost_price || 0);
          return acc + qty * cost;
        }, 0);

        setRaw({
          sales,
          saleItems,
          prevSalesTotal,
          prevCmv,
          prevDaily,
          products,
          clients,
          providers,
          openCompras,
          gastos: gastosPeriodo,
          prevGastosTotal,
        });
      } catch (error) {
        console.error(
          '[ReportesInteligentes] loadData falló:',
          error?.message || error,
          { code: error?.code, details: error?.details, hint: error?.hint },
        );
        toast.error('No se pudieron cargar los reportes');
      } finally {
        // Solo la carga más reciente apaga los spinners, para no cortar el
        // estado de "cargando" de una carga posterior aún en curso.
        if (loadSeqRef.current === seq) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [orgId, dateFrom, dateTo, siteFilter, period],
  );

  const handleRefresh = useCallback(() => {
    if (syncPresetRange()) return;
    loadData({ silent: true });
  }, [syncPresetRange, loadData]);

  useEffect(() => {
    syncPresetRange();
  }, [syncPresetRange]);

  useEffect(() => {
    if (period === 'custom') return undefined;

    const syncIfVisible = () => {
      if (typeof document === 'undefined' || !document.hidden) {
        syncPresetRange();
      }
    };

    window.addEventListener('focus', syncIfVisible);
    document.addEventListener('visibilitychange', syncIfVisible);
    const timer = window.setInterval(syncIfVisible, 60 * 1000);

    return () => {
      window.removeEventListener('focus', syncIfVisible);
      document.removeEventListener('visibilitychange', syncIfVisible);
      window.clearInterval(timer);
    };
  }, [period, syncPresetRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Cuando cambian rango o sucursal, invalidamos los datos pesados ya cargados
  // para forzar un refetch perezoso la próxima vez que se vea la sub-pestaña.
  // `canViewFullClose` tambien invalida: si los permisos se resuelven despues
  // del primer fetch, los turnos se pidieron sin las columnas del arqueo y hay
  // que volver a pedirlos ahora que se sabe que si corresponden.
  useEffect(() => {
    setExtraLoaded({ movimientos: false, produccion: false, turnos: false });
  }, [dateFrom, dateTo, siteFilter, canViewFullClose]);

  // Turnos de caja del periodo. Se cargan solo al entrar a la pestana.
  //
  // El `select` es condicional a proposito: sin permiso de cierre completo, las
  // columnas del arqueo (total_real, diferencia, resumen_cierre) NI SIQUIERA SE
  // PIDEN. Ocultarlas en el render dejaria el dato viajando al navegador, que
  // es el pendiente conocido de Caja.jsx; no hay razon para repetirlo en una
  // pantalla nueva.
  const loadTurnos = useCallback(async () => {
    if (!orgId || !dateFrom || !dateTo) return;
    // Sin permisos resueltos no se pide nada: fallar cerrado.
    if (resolvingCashCloseAccess) return;
    setExtraLoading((s) => ({ ...s, turnos: true }));
    try {
      const bounds = buildSaleQueryBounds(dateFrom, dateTo);
      const columnasBase =
        'id, sitio_id, usuario_email, fecha_apertura, fecha_cierre, estado, saldo_inicial, total_ingresos, total_egresos, total_gastos';
      const columnasArqueo = ', total_real, diferencia, resumen_cierre';
      const rows = await fetchAllPaginated(() => {
        let q = supabase
          .from('turnos_caja')
          .select(canViewFullClose ? columnasBase + columnasArqueo : columnasBase)
          .eq('organization_id', orgId)
          .gte('fecha_apertura', bounds.startIso)
          .lte('fecha_apertura', bounds.endIso)
          .order('fecha_apertura', { ascending: false })
          .order('id', { ascending: true });
        // turnos_caja guarda la sucursal en `sitio_id`, no en `site_id`.
        if (siteFilter) q = q.eq('sitio_id', siteFilter);
        return q;
      });
      setExtra((e) => ({ ...e, turnos: rows || [] }));
      setExtraLoaded((s) => ({ ...s, turnos: true }));
    } catch (e) {
      console.error('[ReportesInteligentes] turnos_caja falló:', e?.message || e);
      setExtra((e) => ({ ...e, turnos: [] }));
      setExtraLoaded((s) => ({ ...s, turnos: true }));
    } finally {
      setExtraLoading((s) => ({ ...s, turnos: false }));
    }
  }, [orgId, dateFrom, dateTo, siteFilter, canViewFullClose, resolvingCashCloseAccess]);

  // Kardex: movimientos de stock del período (carga perezosa).
  const loadMovimientos = useCallback(async () => {
    if (!orgId || !dateFrom || !dateTo) return;
    setExtraLoading((s) => ({ ...s, movimientos: true }));
    try {
      const saleBounds = buildSaleQueryBounds(dateFrom, dateTo);
      const rows = await fetchAllPaginated(() => {
        let q = supabase
          .from('movimientos_stock')
          .select('id, created_at, tipo, cantidad, motivo, producto_id, sitio_origen, sitio_destino, products(name, brand, unit_of_measure)')
          .eq('organization_id', orgId)
          .gte('created_at', saleBounds.startIso)
          .lte('created_at', saleBounds.endIso)
          .order('created_at', { ascending: false });
        if (siteFilter) q = q.or(`sitio_origen.eq.${siteFilter},sitio_destino.eq.${siteFilter}`);
        return q;
      });
      setExtra((e) => ({ ...e, movimientos: rows || [] }));
      setExtraLoaded((s) => ({ ...s, movimientos: true }));
    } catch (e) {
      console.error('[ReportesInteligentes] movimientos_stock falló:', e?.message || e);
      setExtra((e) => ({ ...e, movimientos: [] }));
      setExtraLoaded((s) => ({ ...s, movimientos: true }));
    } finally {
      setExtraLoading((s) => ({ ...s, movimientos: false }));
    }
  }, [orgId, dateFrom, dateTo, siteFilter]);

  // Producción: órdenes del período + recetas (carga perezosa).
  const loadProduccion = useCallback(async () => {
    if (!orgId || !dateFrom || !dateTo) return;
    setExtraLoading((s) => ({ ...s, produccion: true }));
    try {
      const [produccion, recetas] = await Promise.all([
        fetchAllPaginated(() => {
          let q = supabase
            .from('produccion')
            .select('id, producto_id, producto_nombre, cantidad, costo_unitario, costo_total, ingredientes_usados, fecha, sitio_id, activo')
            .eq('organization_id', orgId)
            .eq('activo', true)
            .gte('fecha', dateFrom)
            .lte('fecha', dateTo)
            .order('fecha', { ascending: false });
          if (siteFilter) q = q.eq('sitio_id', siteFilter);
          return q;
        }),
        fetchAllPaginated(() =>
          supabase
            .from('recetas')
            .select('producto_id, rendimiento, merma_pct, ingredientes')
            .eq('organization_id', orgId),
        ),
      ]);
      setExtra((e) => ({ ...e, produccion: produccion || [], recetas: recetas || [] }));
      setExtraLoaded((s) => ({ ...s, produccion: true }));
    } catch (e) {
      console.error('[ReportesInteligentes] produccion/recetas falló:', e?.message || e);
      setExtra((e) => ({ ...e, produccion: [], recetas: [] }));
      setExtraLoaded((s) => ({ ...s, produccion: true }));
    } finally {
      setExtraLoading((s) => ({ ...s, produccion: false }));
    }
  }, [orgId, dateFrom, dateTo, siteFilter]);

  // Dispara la carga perezosa según la sub-pestaña de productos activa.
  useEffect(() => {
    if (mainTab !== 'productos') return;
    // El Kardex (Inventario) y el reporte de Mermas (Producción) usan movimientos_stock.
    const needsMovimientos = productSubTab === 'inventario' || productSubTab === 'produccion';
    if (needsMovimientos && !extraLoaded.movimientos && !extraLoading.movimientos) {
      loadMovimientos();
    }
    if (productSubTab === 'produccion' && !extraLoaded.produccion && !extraLoading.produccion) {
      loadProduccion();
    }
    if (mainTab === 'caja' && !extraLoaded.turnos && !extraLoading.turnos) {
      loadTurnos();
    }
  }, [mainTab, productSubTab, extraLoaded, extraLoading, loadMovimientos, loadProduccion, loadTurnos]);

  // ---------- Calculos derivados ----------
  const metrics = useMemo(() => computeMetrics(raw), [raw]);

  const previousBenefit = raw.prevSalesTotal - raw.prevCmv;
  const deltaVentas = deltaPercent(metrics.ventasTotal, raw.prevSalesTotal);
  const deltaBeneficio = deltaPercent(metrics.beneficio, previousBenefit);
  const deltaResultado = deltaPercent(
    metrics.resultadoNeto,
    previousBenefit - raw.prevGastosTotal,
  );

  // Meta de equilibrio: cuánto hay que facturar para cubrir los gastos FIJOS
  // del período con el margen de contribución actual (ventas − CMV − gastos
  // variables). Misma fórmula que Dashboard y Rentabilidad: utils/puntoEquilibrio.js.
  const breakEven = useMemo(() => calcularPuntoEquilibrio({
    ingresos: metrics.ventasTotal,
    costosVariables: metrics.cmv + (peIncluyeVariables ? metrics.gastosVariables : 0),
    gastosFijos: metrics.gastosFijos,
  }), [metrics.ventasTotal, metrics.cmv, metrics.gastosVariables, metrics.gastosFijos, peIncluyeVariables]);

  // Evolución mensual del punto de equilibrio (últimos 12 meses). No depende
  // del rango de fechas: el equilibrio es mensual (los fijos son del mes).
  // Respeta la sucursal elegida. Agregado del lado de la base para no traer un
  // año de ventas al navegador (RPC get_break_even_monthly).
  const [equilibrioMensual, setEquilibrioMensual] = useState({ meses: [], loading: false, error: false });
  useEffect(() => {
    if (!orgId) return undefined;
    let cancelado = false;
    setEquilibrioMensual((prev) => ({ ...prev, loading: true, error: false }));
    supabase
      .rpc('get_break_even_monthly', {
        p_organization_id: orgId,
        p_site_id: siteFilter || null,
        p_months: 12,
      })
      .then(({ data, error }) => {
        if (cancelado) return;
        if (error) {
          console.error('[ReportesInteligentes] get_break_even_monthly:', error.message);
          setEquilibrioMensual({ meses: [], loading: false, error: true });
          return;
        }
        setEquilibrioMensual({ meses: Array.isArray(data) ? data : [], loading: false, error: false });
      });
    return () => { cancelado = true; };
  }, [orgId, siteFilter]);
  const evolucionEquilibrio = useMemo(
    () => ({
      serie: armarSerieEquilibrio(equilibrioMensual.meses, { incluirVariables: peIncluyeVariables }),
      loading: equilibrioMensual.loading,
      error: equilibrioMensual.error,
      incluyeVariables: peIncluyeVariables,
    }),
    [equilibrioMensual, peIncluyeVariables],
  );

  const vencimientosHoy = snapshot.proyIngresosHoy + snapshot.proyEgresosHoy;

  // Rentabilidad por producto (lista completa). Para ventas de promo/combo,
  // sale_items guarda 1 fila `parent` (resumen, sin costo) + N filas `component`
  // (ingredientes con costo real y revenue prorrateado). Saltamos las filas
  // `parent`: su revenue ya vive en los componentes, y contarla duplicaría la
  // venta y mostraría 100% de margen (ver bug-promo-rentabilidad). Mismo criterio
  // que RentabilidadVentas.jsx.
  const rentabilidadProductos = useMemo(
    () => computeRentabilidadProductos(raw.saleItems),
    [raw.saleItems],
  );

  // Top productos (top 10 por facturación). Reusa rentabilidadProductos para
  // mantener el mismo tratamiento combo-aware (sin filas parent).
  const topProductos = useMemo(
    () => [...rentabilidadProductos].sort((a, b) => b.venta - a.venta).slice(0, 10),
    [rentabilidadProductos],
  );

  // Productos vendidos por unidad — lista completa (no top 10) de TODOS los
  // productos que salieron del inventario en el período. Suma standalones y
  // componentes de combos; descarta filas parent (que son el meta del combo,
  // no una unidad real de inventario). Útil para ver el detalle por producto
  // sin que las promos inflen o duplique cantidades.
  const productosVendidosPorUnidad = useMemo(() => {
    // Costo real de cada combo = suma del costo de sus componentes. Sirve para
    // dar margen a la fila "Promo" cuando mostramos las promos como unidad.
    const comboCostByParent = new Map();
    raw.saleItems.forEach((item) => {
      if (item.combo_role !== 'component') return;
      const parentId = item.parent_combo_product_id;
      if (!parentId) return;
      comboCostByParent.set(
        parentId,
        Number(comboCostByParent.get(parentId) || 0) +
          Number(item.quantity || 0) * Number(item.producto?.cost_price || 0),
      );
    });

    const map = new Map();
    raw.saleItems.forEach((item) => {
      const isPromoParent = item.combo_role === 'parent';
      const isPromoComponent = item.combo_role === 'component';
      if (showPromoProducts) {
        // Mostrando promos: el combo ES la unidad. No contamos las unidades de
        // los productos vendidos dentro del combo (sus componentes).
        if (isPromoComponent) return;
      } else {
        // Destildado: desarmamos el combo en sus componentes y los contamos
        // junto con las ventas sueltas. La fila resumen (parent) no se cuenta.
        if (isPromoParent) return;
      }
      const p = item.producto;
      if (!p?.id) return;
      const isPromo = showPromoProducts && isPromoParent;
      const key = isPromo ? `promo:${p.id}` : `prod:${p.id}`;
      const qty = Number(item.quantity || 0);
      const subtotal = Number(item.subtotal ?? qty * Number(item.unit_price || 0));
      const cost = isPromo
        ? Number(comboCostByParent.get(p.id) || 0)
        : qty * Number(p.cost_price || 0);
      const entry = map.get(key) || {
        id: key,
        nombre: safeName(item.product_name_snapshot || p.name),
        cant: 0,
        venta: 0,
        cmv: 0,
        isPromo,
      };
      entry.cant += qty;
      entry.venta += subtotal;
      entry.cmv += cost;
      map.set(key, entry);
    });
    return Array.from(map.values())
      .map((e) => ({
        ...e,
        beneficio: e.venta - e.cmv,
        margenPct: e.venta > 0 ? ((e.venta - e.cmv) / e.venta) * 100 : 0,
      }))
      .sort((a, b) => b.cant - a.cant || b.venta - a.venta);
  }, [raw.saleItems, showPromoProducts]);

  // Proveedores con deuda — lista de proveedores con saldo > 0 (lo que
  // todavía les debemos) ordenados por monto. Usa getProviderBalanceValue
  // para tolerar el shape histórico del campo.
  const proveedoresDeuda = useMemo(() => {
    return raw.providers
      .map((p) => ({ ...p, _balance: getProviderBalanceValue(p) }))
      .filter((p) => p._balance > 0.01)
      .sort((a, b) => b._balance - a._balance);
  }, [raw.providers]);

  // Próximos vencimientos — compras abiertas con su fecha de vencimiento
  // calculada (fecha + días de condición). Ignora pagos y recibos sin monto.
  // Ordenados por vencimiento ascendente (las más urgentes arriba).
  const vencimientosProveedores = useMemo(() => {
    const provName = new Map(
      raw.providers.map((p) => [p.id, p.razon_social]),
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (raw.openCompras || [])
      .filter((c) => {
        const tipo = String(c?.tipo_comprobante || '').trim().toLowerCase();
        if (tipo === 'pago') return false;
        const monto = Number(c?.total_general || 0);
        return monto > 0;
      })
      .map((c) => {
        const due = getCompraDueDate(c);
        const monto = Number(c?.total_general || 0);
        const overdue = due ? due < today : false;
        return {
          id: c.id,
          proveedor: provName.get(c.proveedor_id) || c.proveedor_nombre || 'Sin proveedor',
          fecha: c.fecha,
          fechaVencimiento: due,
          monto,
          estado: c.estado,
          condicion: c.condicion_pago,
          overdue,
        };
      })
      .sort((a, b) => {
        const ta = a.fechaVencimiento ? a.fechaVencimiento.getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.fechaVencimiento ? b.fechaVencimiento.getTime() : Number.MAX_SAFE_INTEGER;
        return ta - tb;
      });
  }, [raw.openCompras, raw.providers]);

  // Top clientes (del periodo, por monto vendido)
  // Top clientes. El consumidor final generico queda afuera: en un kiosco se
  // lleva el 100% de los tickets y el ranking se convierte en una sola fila que
  // no dice nada. Es mostrador, no un cliente — mismo criterio que el resto del
  // sistema (ver balanceValues.js).
  const topClientes = useMemo(() => {
    const genericos = new Set(
      raw.clients.filter((c) => isGenericConsumidorFinalClient(c)).map((c) => c.id),
    );
    const map = new Map();
    raw.sales.forEach((s) => {
      if (!s.client_id || genericos.has(s.client_id)) return;
      const entry = map.get(s.client_id) || { id: s.client_id, total: 0, count: 0 };
      entry.total += Number(s.total || 0);
      entry.count += 1;
      map.set(s.client_id, entry);
    });
    const clientLookup = new Map(raw.clients.map((c) => [c.id, c]));
    return Array.from(map.values())
      .map((entry) => {
        const cliente = clientLookup.get(entry.id);
        return {
          ...entry,
          nombre: cliente?.full_name || 'Cliente sin nombre',
          balance: cliente ? getClientReceivableValue(cliente) : 0,
        };
      })
      .sort((a, b) => b.total - a.total)
      .slice(0, 10);
  }, [raw.sales, raw.clients]);

  // Ventas por día (chart)
  const ventasPorDia = useMemo(() => {
    // El tramo anterior se alinea POR POSICION (dia 1 contra dia 1), no por
    // fecha de calendario: con "Mes anterior" o un rango custom las fechas no
    // coinciden y superponerlas por fecha dejaria la serie vacia. Si los tramos
    // tienen largos distintos, sobra/falta cola y ahi no hay comparacion.
    return buildDailySeries(raw.sales, dateFrom, dateTo).map((b, i) => {
      const prev = raw.prevDaily[i];
      return {
        ...b,
        label: formatDateShort(b.fecha),
        prevVentas: prev ? prev.ventas : null,
        prevFecha: prev ? prev.fecha : null,
      };
    });
  }, [raw.sales, raw.prevDaily, dateFrom, dateTo]);

  // Comparativa por sucursal. Solo tiene sentido en consolidado: con una
  // sucursal filtrada los datos en memoria ya son de esa sola.
  // OJO: cada tabla guarda la sucursal en una columna distinta — sales.site_id,
  // sale_items.venta.site_id y gastos.sitio_id. Asumir una sola es el error
  // clasico al agrupar acá.
  const comparativaSucursales = useMemo(
    () => computeComparativaSucursales({ raw, siteFilter, sites }),
    [raw, siteFilter, sites],
  );

  // Nombre de sucursal por id (Kardex y arqueos). Tiene que declararse ANTES
  // de cualquier useMemo que lo liste en sus dependencias: el array de deps se
  // evalúa en el render y una const posterior tira "Cannot access before
  // initialization" (IMPULZA-D: tiraba abajo Reportes entero).
  const siteNameById = useMemo(
    () => new Map(sites.map((s) => [String(s.id), s.nombre])),
    [sites],
  );

  // ---------- Caja y turnos ----------
  const arqueos = useMemo(() => {
    const base = computeArqueos({ turnos: extra.turnos, siteNameById });
    // Las ventas se atribuyen por ventana de turno, no por sales.user_id: en
    // dos de las tres organizaciones activas ese campo viene siempre null.
    const porTurno = atribuirVentasATurnos({ turnos: base, sales: raw.sales });
    return base.map((a) => ({
      ...a,
      ventas: porTurno.get(a.id)?.ventas || 0,
      tickets: porTurno.get(a.id)?.tickets || 0,
    }));
  }, [extra.turnos, siteNameById, raw.sales]);

  const resumenArqueos = useMemo(() => computeResumenArqueos(arqueos), [arqueos]);
  const desvioPorCajero = useMemo(() => computeDesvioPorCajero(arqueos), [arqueos]);
  const ventasPorHora = useMemo(
    () => computeVentasPorHora({ sales: raw.sales, dateFrom, dateTo }),
    [raw.sales, dateFrom, dateTo],
  );

  // Distribucion por metodo de pago
  const ventasPorMetodo = useMemo(() => {
    const map = new Map();
    raw.sales.forEach((s) => {
      const name = s.payment_methods?.name || 'Sin método';
      const entry = map.get(name) || { name, value: 0 };
      entry.value += Number(s.total || 0);
      map.set(name, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.value - a.value);
  }, [raw.sales]);

  // Alertas
  const alertas = useMemo(() => {
    const sinStock = [];
    const bajoStock = [];
    const stockSiteId = siteFilter || null;
    raw.products.forEach((p) => {
      const stock = getProductStockForSite(p, stockSiteId);
      // min_stock: si hay sucursal y stock_levels tiene un override para ese site, usarlo.
      // Si no, usar el min_stock global del producto.
      let min = Number(p.min_stock || 0);
      if (stockSiteId && Array.isArray(p.stock_levels)) {
        const level = p.stock_levels.find(
          (l) => String(l?.site_id || '') === String(stockSiteId),
        );
        if (level && level.min_stock != null) min = Number(level.min_stock || 0);
      }
      if (stock <= 0) sinStock.push({ ...p, stock });
      else if (min > 0 && stock <= min) bajoStock.push({ ...p, stock, min });
    });

    const deudores = raw.clients
      .map((c) => ({ ...c, _balance: getClientReceivableValue(c) }))
      .filter((c) => c._balance > 0.01)
      .sort((a, b) => b._balance - a._balance);

    // Productos sin venta en el periodo. Antes era "todo producto activo que no
    // aparece en saleItems", lo que metia en la misma bolsa a los creados ayer y
    // a los que estaban en cero (no se podian vender): en un catalogo grande el
    // numero era enorme y no disparaba ninguna accion. Ahora entra solo el que
    // TUVO la oportunidad de venderse y no se vendio.
    const vendidosIds = new Set();
    raw.saleItems.forEach((item) => {
      if (item.producto?.id) vendidosIds.add(item.producto.id);
    });
    const periodoMs = Math.max(1, range.days) * 24 * 60 * 60 * 1000;
    const finPeriodo = new Date(`${dateTo}T23:59:59`).getTime();
    const sinVenta = raw.products
      .filter((p) => {
        if (vendidosIds.has(p.id)) return false;
        // Sin stock no se podia vender: es un problema de reposicion, no de rotacion.
        if (getProductStockForSite(p, stockSiteId) <= 0) return false;
        // Dado de alta dentro del periodo: todavia no tuvo su ventana completa.
        if (p.created_at) {
          const alta = new Date(p.created_at).getTime();
          if (Number.isFinite(alta) && finPeriodo - alta < periodoMs) return false;
        }
        return true;
      })
      .map((p) => {
        const stock = getProductStockForSite(p, stockSiteId);
        return { ...p, stock, valorInmovilizado: stock * getCatalogUnitCost(p) };
      })
      .sort((a, b) => b.valorInmovilizado - a.valorInmovilizado);

    // Lo accionable no es cuantos son, sino cuanta plata quieta representan.
    const sinVentaValor = sinVenta.reduce((acc, p) => acc + p.valorInmovilizado, 0);

    return { sinStock, bajoStock, deudores, sinVenta, sinVentaValor };
  }, [raw.products, raw.clients, raw.saleItems, siteFilter, range.days, dateTo]);

  // ===== Inventario y control de stock =====

  // Stock valorizado: stock actual × costo y × precio de venta. Capital inmovilizado.
  const stockValorizado = useMemo(() => {
    const stockSiteId = siteFilter || null;
    const rows = raw.products
      .map((p) => {
        const stock = getProductStockForSite(p, stockSiteId);
        const cost = getCatalogUnitCost(p);
        // undefined (no 0): permite usar el usd_exchange_rate del producto en vez de
        // valuar un producto en USD con su sell_price ARS viejo. Bug P4. (Ideal:
        // pasar la tasa vigente de la organización como hace Inventario.jsx.)
        const price = getCatalogSalePrice(p);
        return {
          id: p.id,
          nombre: safeName(p.name),
          stock,
          cost,
          price,
          valorCosto: stock * cost,
          valorVenta: stock * price,
        };
      })
      .filter((r) => Math.abs(r.stock) > 0.0001)
      .sort((a, b) => b.valorCosto - a.valorCosto);
    const totalCosto = rows.reduce((a, r) => a + r.valorCosto, 0);
    const totalVenta = rows.reduce((a, r) => a + r.valorVenta, 0);
    const totalUnidades = rows.reduce((a, r) => a + r.stock, 0);
    return { rows, totalCosto, totalVenta, totalUnidades };
  }, [raw.products, siteFilter]);

  // Punto de pedido: productos agotados o por debajo del mínimo.
  const puntoPedido = useMemo(() => {
    const stockSiteId = siteFilter || null;
    return raw.products
      .map((p) => {
        const stock = getProductStockForSite(p, stockSiteId);
        let min = Number(p.min_stock || 0);
        if (stockSiteId && Array.isArray(p.stock_levels)) {
          const lvl = p.stock_levels.find((l) => String(l?.site_id || '') === String(stockSiteId));
          if (lvl && lvl.min_stock != null) min = Number(lvl.min_stock || 0);
        }
        return { id: p.id, nombre: safeName(p.name), stock, min };
      })
      .filter((r) => r.stock <= 0 || (r.min > 0 && r.stock <= r.min))
      .map((r) => ({
        ...r,
        agotado: r.stock <= 0,
        faltante: r.min > 0 ? Math.max(r.min - r.stock, 0) : 0,
      }))
      .sort((a, b) => (a.agotado === b.agotado ? b.faltante - a.faltante : a.agotado ? -1 : 1));
  }, [raw.products, siteFilter]);

  // Rotación y stock inmovilizado: vendido en el período vs stock actual.
  // "3,2x" no dispara ninguna accion; "te quedan 11 dias de stock" si. Por eso
  // el orden por defecto es por cobertura ascendente: lo que se va a cortar
  // primero arriba. Los inmovilizados (cobertura infinita) van al final.
  const rotacion = useMemo(
    () => computeRotacion({
      products: raw.products,
      rentabilidadProductos,
      siteId: siteFilter || null,
      dias: range.days,
    }),
    [raw.products, rentabilidadProductos, siteFilter, range.days],
  );

  // ===== Facturación y rentabilidad =====

  // Ventas por categoría / rubro (combo-aware, salta filas parent).
  const ventasPorCategoria = useMemo(() => {
    const catNameById = new Map(
      raw.products.map((p) => [p.id, p.product_categories?.name || 'Sin categoría']),
    );
    const map = new Map();
    raw.saleItems.forEach((item) => {
      if (item.combo_role === 'parent') return;
      const p = item.producto;
      if (!p?.id) return;
      const cat = catNameById.get(p.id) || 'Sin categoría';
      const qty = Number(item.quantity || 0);
      const subtotal = Number(item.subtotal ?? qty * Number(item.unit_price || 0));
      const cost = qty * Number(p.cost_price || 0);
      const entry = map.get(cat) || { name: cat, value: 0, cmv: 0 };
      entry.value += subtotal;
      entry.cmv += cost;
      map.set(cat, entry);
    });
    const arr = Array.from(map.values())
      .map((e) => ({
        ...e,
        beneficio: e.value - e.cmv,
        margenPct: e.value > 0 ? ((e.value - e.cmv) / e.value) * 100 : 0,
      }))
      .sort((a, b) => b.value - a.value);
    const total = arr.reduce((a, e) => a + e.value, 0);
    return arr.map((e) => ({ ...e, pct: total > 0 ? (e.value / total) * 100 : 0 }));
  }, [raw.saleItems, raw.products]);

  // ===== Kardex =====
  const productosCatalogo = useMemo(
    () =>
      raw.products
        .map((p) => ({ id: p.id, name: safeName(p.name), brand: p.brand }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [raw.products],
  );

  const kardexProductos = useMemo(() => {
    const term = kardexQuery.trim().toLowerCase();
    if (!term) return productosCatalogo.slice(0, 50);
    return productosCatalogo.filter((p) => p.name.toLowerCase().includes(term)).slice(0, 60);
  }, [productosCatalogo, kardexQuery]);

  const kardexMovimientos = useMemo(() => {
    if (!kardexProductId) return [];
    return (extra.movimientos || [])
      .filter((m) => m.producto_id === kardexProductId)
      .map((m) => {
        const cls = classifyTipoMovimiento(m.tipo);
        const sitio =
          cls === 'egreso'
            ? siteNameById.get(String(m.sitio_origen)) || ''
            : siteNameById.get(String(m.sitio_destino)) ||
              siteNameById.get(String(m.sitio_origen)) ||
              '';
        return { ...m, _cls: cls, _sitio: sitio };
      });
  }, [extra.movimientos, kardexProductId, siteNameById]);

  // ===== Producción =====

  // Consumo de insumos: suma de ingredientes_usados de las órdenes del período.
  // Shape real de cada item: { id, nombre, necesario (cantidad), costoUnitarioInsumo }.
  const consumoInsumos = useMemo(() => {
    const costByProductId = new Map(raw.products.map((p) => [p.id, getCatalogUnitCost(p)]));
    const map = new Map();
    (extra.produccion || []).forEach((ord) => {
      const items = Array.isArray(ord.ingredientes_usados) ? ord.ingredientes_usados : [];
      items.forEach((it) => {
        const pid = it.id || it.product_id || it.producto_id || null;
        const key = pid || it.nombre || it.name;
        if (key == null) return;
        const qty = Number(it.necesario ?? it.cantidad ?? it.quantity ?? it.qty ?? 0);
        if (!(qty > 0)) return;
        const unitCost =
          Number(it.costoUnitarioInsumo ?? it.costo_unitario ?? 0) ||
          (pid != null ? Number(costByProductId.get(pid) || 0) : 0);
        const cost = Number(it.costo ?? it.cost ?? unitCost * qty);
        const entry = map.get(key) || {
          id: String(key),
          nombre: safeName(it.nombre || it.name || 'Insumo'),
          cant: 0,
          costo: 0,
        };
        entry.cant += qty;
        entry.costo += cost;
        map.set(key, entry);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.costo - a.costo || b.cant - a.cant);
  }, [extra.produccion, raw.products]);

  // Desviación de costos: costo estándar (cost_price del producto) vs costo real
  // registrado en la orden de producción.
  const desviacionCostos = useMemo(() => {
    const costByProductId = new Map(raw.products.map((p) => [p.id, getCatalogUnitCost(p)]));
    const map = new Map();
    (extra.produccion || []).forEach((ord) => {
      const key = ord.producto_id || ord.producto_nombre;
      if (key == null) return;
      const cant = Number(ord.cantidad || 0);
      const realTotal = Number(ord.costo_total ?? Number(ord.costo_unitario || 0) * cant);
      const entry = map.get(key) || {
        id: String(key),
        nombre: safeName(ord.producto_nombre || 'Producto'),
        cantidad: 0,
        realTotal: 0,
        teoricoUnit: Number(costByProductId.get(ord.producto_id) || 0),
      };
      entry.cantidad += cant;
      entry.realTotal += realTotal;
      map.set(key, entry);
    });
    return Array.from(map.values())
      .map((e) => {
        const realUnit = e.cantidad > 0 ? e.realTotal / e.cantidad : 0;
        const teoricoTotal = e.teoricoUnit * e.cantidad;
        const desvio = e.realTotal - teoricoTotal;
        const desvioPct = teoricoTotal > 0 ? (desvio / teoricoTotal) * 100 : 0;
        return { ...e, realUnit, teoricoTotal, desvio, desvioPct };
      })
      .sort((a, b) => Math.abs(b.desvio) - Math.abs(a.desvio));
  }, [extra.produccion, raw.products]);

  // Mermas y desperdicios: movimientos de stock reconocidos como merma.
  const mermas = useMemo(() => {
    const map = new Map();
    (extra.movimientos || []).filter(isMermaMovimiento).forEach((m) => {
      const key = m.producto_id || m.products?.name;
      if (key == null) return;
      const qty = Math.abs(Number(m.cantidad || 0));
      const entry = map.get(key) || {
        id: String(key),
        nombre: safeName(m.products?.name || 'Producto'),
        cant: 0,
        eventos: 0,
        ultimo: null,
        ultimoMotivo: '',
      };
      entry.cant += qty;
      entry.eventos += 1;
      if (!entry.ultimo || new Date(m.created_at) > new Date(entry.ultimo)) {
        entry.ultimo = m.created_at;
        entry.ultimoMotivo = m.motivo || '';
      }
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => b.cant - a.cant);
  }, [extra.movimientos]);

  const exportResumenCsv = () => {
    const rows = [
      ['Metrica', 'Valor'],
      ['Periodo', periodLabel],
      ['Desde', dateFrom],
      ['Hasta', dateTo],
      ['Sucursal', selectedSite?.nombre || 'Consolidado'],
      ['Ventas', metrics.ventasTotal.toFixed(2)],
      ['Tickets', metrics.ticketsCount],
      ['Ticket promedio', metrics.ticketPromedio.toFixed(2)],
      ['Costo mercaderia (CMV)', metrics.cmv.toFixed(2)],
      ['Beneficio bruto', metrics.beneficio.toFixed(2)],
      ['Margen %', metrics.margenPct.toFixed(2)],
      ['Cuentas por cobrar', metrics.aCobrar.toFixed(2)],
      ['Cuentas por pagar', metrics.aPagar.toFixed(2)],
      ['Gastos del periodo', metrics.gastosPeriodo.toFixed(2)],
      ['Saldo financiero (bancos)', snapshot.saldoCaja.toFixed(2)],
      ['Gastos fijos del periodo', metrics.gastosFijos.toFixed(2)],
      ['Gastos variables del periodo', metrics.gastosVariables.toFixed(2)],
      ['Margen de contribucion %', breakEven.margenContribucionPct.toFixed(2)],
      ['Meta de equilibrio', breakEven.puntoEquilibrio == null ? (breakEven.estado === 'no_alcanzable' ? 'No alcanzable' : 'Sin datos') : breakEven.puntoEquilibrio.toFixed(2)],
      ['Productos sin stock', alertas.sinStock.length],
      ['Productos bajo minimo', alertas.bajoStock.length],
      ['Clientes con deuda', alertas.deudores.length],
      ['Productos sin venta', alertas.sinVenta.length],
      ['Pedidos web pendientes', snapshot.pedidosWeb],
      ['Productos con baja rentabilidad', snapshot.bajaRentabilidad],
      ['Vencimientos proyectados hoy', vencimientosHoy],
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell || '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sitePart = selectedSite ? `_${selectedSite.nombre.replace(/\s+/g, '-').toLowerCase()}` : '_consolidado';
    link.download = `reportes-inteligentes${sitePart}_${dateFrom}_${dateTo}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Resumen exportado');
  };

  // ---------- Render ----------
  return (
    <div className="min-h-screen bg-[#F6F8FB] text-slate-800 p-4 md:p-6 lg:p-8">
      <div className="max-w-[1500px] mx-auto space-y-5">
        <header className="space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                <FileSpreadsheet size={14} className="text-[#51A7FD]" />
                Centro de reportes
              </div>
              <h1 className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-slate-950">
                Reportes inteligentes
              </h1>
              <p className="mt-1 text-sm font-medium text-slate-500">
                Indicadores, alertas y rankings del negocio en tiempo real.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={loading || refreshing}
                className="p-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:shadow-md transition-all disabled:opacity-50"
                title="Refrescar"
                aria-label="Refrescar"
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
              </button>
              <button
                type="button"
                onClick={exportResumenCsv}
                disabled={loading}
                className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
              >
                <Download size={14} /> CSV
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex gap-1 bg-slate-50 rounded-2xl p-1 flex-wrap">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => applyPreset(p.id)}
                  className={`px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                    period === p.id
                      ? 'bg-slate-950 text-white shadow-sm'
                      : 'text-slate-400 hover:text-slate-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              {period === 'custom' && (
                <span className="px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest bg-[#51A7FD]/10 text-[#51A7FD]">
                  Custom
                </span>
              )}
            </div>

            <div className="flex gap-2 items-end">
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => handleDateChange('from', e.target.value)}
                  className="w-36 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black outline-none text-slate-700 focus:border-[#51A7FD]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-black uppercase tracking-widest text-slate-400">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  onChange={(e) => handleDateChange('to', e.target.value)}
                  className="w-36 px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black outline-none text-slate-700 focus:border-[#51A7FD]"
                />
              </div>
            </div>

            <div className="space-y-1 flex-1 lg:flex-initial lg:min-w-[220px]">
              <label className="text-[9px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1">
                <MapPin size={10} /> Sucursal
              </label>
              <select
                value={siteFilter}
                onChange={(e) => setSiteFilter(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[11px] font-black uppercase tracking-widest outline-none text-slate-700 focus:border-[#51A7FD]"
              >
                <option value="">Consolidado (todas)</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:ml-auto text-right">
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Vista</p>
              <p className="text-[11px] font-black text-slate-700 truncate max-w-[260px]">
                {selectedSite ? selectedSite.nombre : 'Todas las sucursales'} · {range.days}d
              </p>
            </div>
          </div>
        </header>

        {/* Pestañas principales */}
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-2xl p-1 shadow-sm w-fit">
          {[
            { id: 'resumen', label: 'Resumen', icon: BarChart3 },
            { id: 'productos', label: 'Productos', icon: Package },
            { id: 'caja', label: 'Caja y turnos', icon: Wallet },
          ].map((t) => {
            const TabIcon = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setMainTab(t.id)}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                  mainTab === t.id ? 'bg-slate-950 text-white shadow-sm' : 'text-slate-400 hover:text-slate-700'
                }`}
              >
                <TabIcon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Sub-pestañas de Productos */}
        {mainTab === 'productos' && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-2xl p-1 w-fit flex-wrap">
            {[
              { id: 'inventario', label: 'Inventario y stock', icon: Warehouse },
              { id: 'rentabilidad', label: 'Facturación y rentabilidad', icon: Percent },
              { id: 'produccion', label: 'Producción', icon: Factory },
            ].map((t) => {
              const TabIcon = t.icon;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setProductSubTab(t.id)}
                  className={`px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${
                    productSubTab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400 hover:text-slate-700'
                  }`}
                >
                  <TabIcon size={13} /> {t.label}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-32 rounded-2xl bg-white border border-slate-100 animate-pulse" />
            ))}
          </div>
        ) : mainTab === 'caja' ? (
          <TabCaja
            arqueos={arqueos}
            blindCashCloseEnabled={blindCashCloseEnabled}
            canViewFullClose={canViewFullClose}
            cargando={extraLoading.turnos || resolvingCashCloseAccess}
            desvioPorCajero={desvioPorCajero}
            periodLabel={periodLabel}
            resumenArqueos={resumenArqueos}
            ventasPorHora={ventasPorHora}
          />
        ) : mainTab === 'resumen' ? (
          <TabResumen
            alertas={alertas}
            breakEven={breakEven}
            evolucionEquilibrio={evolucionEquilibrio}
            comparativaSucursales={comparativaSucursales}
            deltaBeneficio={deltaBeneficio}
            deltaResultado={deltaResultado}
            deltaVentas={deltaVentas}
            metrics={metrics}
            navigate={navigate}
            periodLabel={periodLabel}
            proveedoresDeuda={proveedoresDeuda}
            range={range}
            setShowPrevSeries={setShowPrevSeries}
            setViewDeudaProveedores={setViewDeudaProveedores}
            setViewTopClientes={setViewTopClientes}
            showPrevSeries={showPrevSeries}
            snapshot={snapshot}
            topClientes={topClientes}
            vencimientosHoy={vencimientosHoy}
            vencimientosProveedores={vencimientosProveedores}
            ventasPorDia={ventasPorDia}
            ventasPorMetodo={ventasPorMetodo}
            viewDeudaProveedores={viewDeudaProveedores}
            viewTopClientes={viewTopClientes}
          />
        ) : (
          <>
            {/* ===================== PESTAÑA PRODUCTOS ===================== */}

            {/* ---------- Inventario y control de stock ---------- */}
            {productSubTab === 'inventario' && (
              <TabInventario
                alertas={alertas}
                extraLoading={extraLoading}
                kardexMovimientos={kardexMovimientos}
                kardexProductId={kardexProductId}
                kardexProductos={kardexProductos}
                kardexQuery={kardexQuery}
                puntoPedido={puntoPedido}
                range={range}
                rotacion={rotacion}
                setKardexProductId={setKardexProductId}
                setKardexQuery={setKardexQuery}
                stockValorizado={stockValorizado}
              />
            )}

            {/* ---------- Facturación y rentabilidad ---------- */}
            {productSubTab === 'rentabilidad' && (
              <TabRentabilidad
                productosVendidosPorUnidad={productosVendidosPorUnidad}
                rentabilidadProductos={rentabilidadProductos}
                setShowPromoProducts={setShowPromoProducts}
                setViewCategorias={setViewCategorias}
                setViewTopProductos={setViewTopProductos}
                setViewUnidades={setViewUnidades}
                showPromoProducts={showPromoProducts}
                ventasPorCategoria={ventasPorCategoria}
                viewCategorias={viewCategorias}
                viewTopProductos={viewTopProductos}
                viewUnidades={viewUnidades}
              />
            )}

            {/* ---------- Producción ---------- */}
            {productSubTab === 'produccion' && (
              <TabProduccion
                consumoInsumos={consumoInsumos}
                desviacionCostos={desviacionCostos}
                extraLoaded={extraLoaded}
                extraLoading={extraLoading}
                mermas={mermas}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
